<script>
import { mapGetters } from 'vuex';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import {
  canReadResource,
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import AccountContext from './sidebarComponents/AccountContext.vue';
import SidebarHeader from './sidebarComponents/SidebarHeader.vue';
import SidebarFooter from './sidebarComponents/SidebarFooter.vue';
import SidebarSecondaryList from './sidebarComponents/SidebarSecondaryList.vue';
import SidebarSecondaryItem from './sidebarComponents/SidebarSecondaryItem.vue';
import SidebarInboxItem from './sidebarComponents/SidebarInboxItem.vue';

export default {
  components: {
    AccountContext,
    SidebarHeader,
    SidebarFooter,
    SidebarSecondaryList,
    SidebarSecondaryItem,
    SidebarInboxItem,
  },
  props: {
    isACustomBrandedInstance: {
      type: Boolean,
      default: false,
    },
    logoSource: {
      type: String,
      default: '',
    },
    installationName: {
      type: String,
      default: '',
    },
    accountId: {
      type: Number,
      default: 0,
    },
    menuItems: {
      type: Array,
      default: () => [],
    },
    activeMenuItem: {
      type: String,
      default: '',
    },
    forceExpanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isCollapsed: false,
      showOptionsMenu: false,
      showAllLabels: false,
      showAllInboxes: false,
      showAllContactLabels: false,
      showAllTeams: false,
      showAllFilters: false,
      showScheduleMessageModal: false,
      expandedMenuKeys: [],
      labelSearchQuery: '',
      showLabelSearch: false,
      INBOX_TYPES,
    };
  },
  watch: {
    forceExpanded: {
      handler() {
        this.handleResize();
      },
      immediate: true,
    },
    activeMenuItem: {
      handler(newVal) {
        if (newVal && !this.expandedMenuKeys.includes(newVal)) {
          this.expandedMenuKeys.push(newVal);
        }
      },
      immediate: true,
    },
    // Contador do chat interno (#1748): carregado quando o chat fica disponível — e não no
    // `mounted`, porque no boot as flags da conta ainda podem não ter chegado. Daí em diante
    // quem move o número é o socket (actionCable.js).
    //
    // Visão consolidada (#1752): a lista das outras contas da pessoa vem junto, e o badge soma as
    // não lidas de todas — só com o chat disponível NESTA conta.
    internalChatAvailable: {
      handler(available) {
        if (available) {
          this.$store.dispatch('internalChat/fetchChannels', {
            accountId: this.accountId,
          });
          this.$store.dispatch('internalChat/fetchAccounts');
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.handleResize();
    this.$store.dispatch('notifications/unReadCount');
    this.$store.dispatch('notifications/summary');
    // `tasks/counts` cobra a mesma permissao da lista de tarefas (#1625).
    if (
      canReadResource(
        'tasks',
        getUserPermissions(this.currentUser, this.accountId)
      )
    ) {
      this.$store.dispatch('tasks/fetchCounts');
    }
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    ...mapGetters({
      activeInbox: 'getSelectedInbox',
      currentUser: 'getCurrentUser',
      currentRole: 'getCurrentRole',
      taskCounts: 'tasks/getCounts',
      internalChatUnread: 'internalChat/getUnreadCount',
      internalChatOtherUnread: 'internalChat/getOtherAccountsUnreadCount',
      internalChatMentioned: 'internalChat/hasUnreadMention',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    internalChatAvailable() {
      return (
        !!this.accountId &&
        !!this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.INTERNAL_CHAT
        ) &&
        canReadResource(
          'internalChat',
          getUserPermissions(this.currentUser, this.accountId)
        )
      );
    },
    // Contadores ao lado do rótulo de um item do menu, por `badgeKey` (primaryMenu.js). Cada
    // entrada é uma lista de pílulas, na ordem em que aparecem; pílula com zero não aparece.
    menuBadges() {
      const pill = (key, count, tooltip, colorClass) => ({
        key,
        count,
        label: count > 99 ? '99+' : String(count),
        tooltip,
        colorClass,
      });
      const visible = pills => pills.filter(item => item.count > 0);
      const { due_today: dueToday = 0, overdue = 0 } = this.taskCounts || {};
      const chatUnread = this.internalChatUnread || 0;
      const chatOther = this.internalChatOtherUnread || 0;
      const chatTooltip = [
        this.$t('SIDEBAR.INTERNAL_CHAT_UNREAD', { count: chatUnread }),
        chatOther > 0 &&
          this.$t('SIDEBAR.INTERNAL_CHAT_UNREAD_OTHER_ACCOUNTS', {
            count: chatOther,
          }),
      ]
        .filter(Boolean)
        .join(' · ');
      return {
        my_tasks: visible([
          pill(
            'due_today',
            dueToday,
            this.$t('SIDEBAR.MY_TASKS_DUE_TODAY', { count: dueToday }),
            'bg-orange-500'
          ),
          pill(
            'overdue',
            overdue,
            this.$t('SIDEBAR.MY_TASKS_OVERDUE', { count: overdue }),
            'bg-red-500'
          ),
        ]),
        // UM número: o total de todas as contas da visão consolidada (#1752). O tooltip diz
        // quanto dele é de outras contas — quem abre o chat vê a divisão por conta lá dentro.
        // Antes dele, um "@" quando alguma não lida cita a pessoa (#1784).
        internal_chat: visible([
          {
            ...pill(
              'mention',
              this.internalChatMentioned && chatUnread > 0 ? 1 : 0,
              this.$t('SIDEBAR.INTERNAL_CHAT_MENTIONED'),
              'bg-brand-solid'
            ),
            label: '@',
          },
          pill('unread', chatUnread, chatTooltip, 'bg-brand-solid'),
        ]),
      };
    },
    labels() {
      const conversationsMenu = this.menuItems.find(
        m => m.key === 'conversations'
      );
      if (!conversationsMenu) return [];
      const labelsItem = conversationsMenu.children.find(
        c => c.key === 'labels'
      );
      return labelsItem ? labelsItem.children : [];
    },
    filteredLabels() {
      if (!this.labelSearchQuery) return this.labels;
      const query = this.labelSearchQuery.toLowerCase();
      return this.labels.filter(label =>
        label.label.toLowerCase().includes(query)
      );
    },
    showAllMap() {
      return {
        'contact-labels': this.showAllContactLabels,
        teams: this.showAllTeams,
        filters: this.showAllFilters,
      };
    },
  },
  methods: {
    handleResize() {
      if (this.forceExpanded) {
        this.isCollapsed = false;
        return;
      }
      if (window.innerWidth < 1200) {
        this.isCollapsed = true;
      } else {
        this.isCollapsed = false;
      }
    },
    toggleSidebar() {
      if (window.innerWidth < 768) {
        this.$emit('toggleSidebar');
        return;
      }
      this.isCollapsed = !this.isCollapsed;
    },
    toggleOptions() {
      this.showOptionsMenu = !this.showOptionsMenu;
    },
    toggleAccountModal() {
      this.$emit('toggleAccounts');
    },
    toggleSupportChatWindow() {
      window.$chatwoot.toggle();
    },
    openNotificationPanel() {
      this.$track(ACCOUNT_EVENTS.OPENED_NOTIFICATIONS);
      this.$emit('openNotificationPanel');
    },
    handleNewClick(menuItem) {
      if (menuItem.showModalForNewItem) {
        if (menuItem.modalName === 'ScheduleMessageModal') {
          this.showScheduleMessageModal = true;
        } else {
          this.$emit('openModal', menuItem.modalName);
        }
      } else {
        this.$router.push(menuItem.toState);
      }
    },
    toggleShowAll(key) {
      if (key === 'contact-labels') {
        this.showAllContactLabels = !this.showAllContactLabels;
      } else if (key === 'teams') {
        this.showAllTeams = !this.showAllTeams;
      } else if (key === 'filters') {
        this.showAllFilters = !this.showAllFilters;
      }
    },
    toggleShowAllLabels() {
      this.showAllLabels = !this.showAllLabels;
    },
    toggleShowAllInboxes() {
      this.showAllInboxes = !this.showAllInboxes;
    },
    toggleLabelSearch() {
      this.showLabelSearch = !this.showLabelSearch;
      if (!this.showLabelSearch) {
        this.labelSearchQuery = '';
      }
    },
    toggleSecondaryMenu(key) {
      if (this.expandedMenuKeys.includes(key)) {
        this.expandedMenuKeys = this.expandedMenuKeys.filter(k => k !== key);
      } else {
        this.expandedMenuKeys.push(key);
      }
    },
    isSecondaryItemActive(secondaryItem, isRouteActive) {
      if (typeof secondaryItem.isActive === 'function') {
        return secondaryItem.isActive(this.$route);
      }

      const isConversationItem =
        secondaryItem.toStateName === 'home' ||
        ['conversation_mentions', 'conversation_unattended'].includes(
          secondaryItem.toStateName
        );

      if (isConversationItem && this.activeInbox) return false;

      if (isRouteActive) return true;

      return false;
    },
    getInboxStatusColor(status) {
      if (!status) return '';
      switch (status.toLowerCase()) {
        case 'open':
        case 'connected':
          return 'text-green-500';
        case 'close':
        case 'disconnected':
          return 'text-red-500';
        case 'connecting':
        case 'loading':
          return 'text-yellow-500';
        default:
          return 'text-red-600';
      }
    },
    getInboxStatusText(status) {
      const s = (status || 'error').toLowerCase();
      switch (s) {
        case 'open':
        case 'connected':
          return this.$t('INBOX_MGMT.SETTINGS_POPUP.INSTANCE_STATUS_CONNECTED');
        case 'close':
        case 'disconnected':
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.INSTANCE_STATUS_DISCONNECTED'
          );
        case 'connecting':
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.INSTANCE_STATUS_CONNECTING'
          );
        case 'loading':
          return 'Carregando...';
        case 'auth_error':
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.INSTANCE_STATUS_AUTH_ERROR'
          );
        case 'not_found':
          return this.$t('INBOX_MGMT.SETTINGS_POPUP.INSTANCE_STATUS_NOT_FOUND');
        case 'api_error':
          return this.$t('INBOX_MGMT.SETTINGS_POPUP.INSTANCE_STATUS_API_ERROR');
        case 'connection_error':
          return this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.INSTANCE_STATUS_CONNECTION_ERROR'
          );
        case 'timeout':
          return this.$t('INBOX_MGMT.SETTINGS_POPUP.INSTANCE_STATUS_TIMEOUT');
        default:
          return this.$t('INBOX_MGMT.SETTINGS_POPUP.INSTANCE_STATUS_ERROR');
      }
    },
    canShowNewButton(secondaryItem) {
      if (!secondaryItem.newLink) {
        return false;
      }
      if (this.currentRole === 'administrator') {
        return true;
      }
      const permissionsMap = {
        inboxes: ['inbox_manage', 'inbox_create'],
        labels: ['label_manage', 'label_create'],
        'contact-labels': ['label_manage', 'label_create'],
        teams: ['team_manage'],
      };
      const requiredPermissions = permissionsMap[secondaryItem.key];
      if (!requiredPermissions) {
        return false;
      }
      const userPermissions = getUserPermissions(
        this.currentUser,
        this.accountId
      );
      return hasPermissions(requiredPermissions, userPermissions);
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col h-full min-h-0 shrink-0 bg-primary-950 transition-all duration-300"
    :class="isCollapsed ? 'w-20' : 'w-sidebar'"
  >
    <!-- Header: Logo + collapse -->
    <SidebarHeader
      :is-collapsed="isCollapsed"
      :is-a-custom-branded-instance="isACustomBrandedInstance"
      :logo-source="logoSource"
      :installation-name="installationName"
      :account-id="accountId"
      @toggleSidebar="toggleSidebar"
    />

    <!-- Account Context -->
    <div v-if="!isCollapsed" class="px-3">
      <AccountContext @toggleAccounts="toggleAccountModal" />
    </div>

    <!-- Navigation: Scrollable -->
    <nav
      class="flex flex-col flex-1 min-h-0 space-y-1 overflow-y-auto custom-scrollbar"
      :class="isCollapsed ? 'p-2' : 'p-4'"
    >
      <template v-for="menuItem in menuItems">
        <router-link
          :key="menuItem.toState"
          v-slot="{ href, isActive, navigate }"
          :to="menuItem.toState"
          custom
        >
          <div
            class="flex mt-1 items-center rounded-lg text-primary-100 hover:bg-primary-900/40 transition-colors"
            :class="{
              'bg-primary-900/60 text-white':
                isActive || menuItem.key === activeMenuItem,
              'justify-center': isCollapsed,
            }"
          >
            <a
              v-tooltip="{
                content: isCollapsed ? $t(`SIDEBAR.${menuItem.label}`) : '',
                placement: 'right',
                boundariesElement: 'viewport',
                container: 'body',
              }"
              :href="href"
              class="flex items-center gap-3 py-3"
              :class="isCollapsed ? 'px-2 justify-center' : 'flex-1 px-4'"
              :data-testid="`sidebar-menu-${menuItem.key}`"
              @click="navigate"
            >
              <fluent-icon
                :icon="menuItem.icon"
                :class="{
                  'text-white': isActive || menuItem.key === activeMenuItem,
                }"
              />
              <span v-if="!isCollapsed" class="font-medium">{{
                $t(`SIDEBAR.${menuItem.label}`)
              }}</span>
              <div
                v-if="
                  !isCollapsed &&
                  menuItem.badgeKey &&
                  (menuBadges[menuItem.badgeKey] || []).length
                "
                class="flex gap-1 ml-auto"
                :data-testid="`sidebar-badges-${menuItem.badgeKey}`"
              >
                <span
                  v-for="badge in menuBadges[menuItem.badgeKey]"
                  :key="badge.key"
                  v-tooltip.right="badge.tooltip"
                  class="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-bold leading-none text-white"
                  :class="badge.colorClass"
                >
                  {{ badge.label }}
                </span>
              </div>
            </a>
            <woot-button
              v-if="
                !isCollapsed &&
                menuItem.children &&
                menuItem.children.length > 0
              "
              type="button"
              size="tiny"
              variant="clear"
              color-scheme="white"
              class="mr-2 transition-all duration-200"
              :icon="
                expandedMenuKeys.includes(menuItem.key)
                  ? 'chevron-up'
                  : 'chevron-down'
              "
              @click="toggleSecondaryMenu(menuItem.key)"
            />
          </div>
        </router-link>

        <!-- Secondary menu (expanded) -->
        <div
          v-if="!isCollapsed && expandedMenuKeys.includes(menuItem.key)"
          :key="`secondary-menu-${menuItem.key}`"
          class="pl-4 ml-5 border-l-2 border-primary-900/40"
        >
          <template v-for="secondaryItem in menuItem.children">
            <!-- Labels (with search) -->
            <SidebarSecondaryList
              v-if="secondaryItem.hasSubMenu && secondaryItem.key === 'labels'"
              :key="secondaryItem.key"
              :secondary-item="secondaryItem"
              :children="secondaryItem.children"
              :show-all="showAllLabels"
              :can-show-new-button="canShowNewButton(secondaryItem)"
              enable-search
              :show-search="showLabelSearch"
              :search-query="labelSearchQuery"
              :filtered-children="filteredLabels"
              @toggle-show-all="toggleShowAllLabels"
              @toggle-search="toggleLabelSearch"
              @new-click="handleNewClick"
              @update:searchQuery="labelSearchQuery = $event"
            >
              <template #item="{ items }">
                <router-link
                  v-for="child in items"
                  :key="child.id"
                  v-slot="{
                    href: childHref,
                    isActive: childIsActive,
                    navigate: childNavigate,
                  }"
                  :to="child.toState"
                  custom
                >
                  <a
                    :href="childHref"
                    class="flex mt-1 items-center gap-3 px-4 py-2 rounded-lg text-primary-100 hover:bg-primary-900/40 transition-colors"
                    :class="{ 'bg-primary-900/40 text-white': childIsActive }"
                    @click="childNavigate"
                  >
                    <!-- `shrink-0`: tamanho fixo nao impede encolhimento em
                         flexbox, e o padrao de `flex-shrink` e 1. Sem ele, um
                         nome de etiqueta longo espremia o quadrado ate virar
                         uma barra vertical; quem tem de ceder e o texto, que ja
                         trunca. -->
                    <span
                      v-if="child.color"
                      class="w-3 h-3 rounded-sm shrink-0"
                      :style="{ backgroundColor: child.color }"
                    />
                    <span class="font-medium truncate">{{ child.label }}</span>
                  </a>
                </router-link>
              </template>
              <template #item-remaining="{ items }">
                <router-link
                  v-for="child in items"
                  :key="child.id"
                  v-slot="{
                    href: childHref,
                    isActive: childIsActive,
                    navigate: childNavigate,
                  }"
                  :to="child.toState"
                  custom
                >
                  <a
                    :href="childHref"
                    class="flex mt-1 items-center gap-3 px-4 py-2 rounded-lg text-primary-100 hover:bg-primary-900/40 transition-colors"
                    :class="{ 'bg-primary-900/40 text-white': childIsActive }"
                    @click="childNavigate"
                  >
                    <!-- `shrink-0`: tamanho fixo nao impede encolhimento em
                         flexbox, e o padrao de `flex-shrink` e 1. Sem ele, um
                         nome de etiqueta longo espremia o quadrado ate virar
                         uma barra vertical; quem tem de ceder e o texto, que ja
                         trunca. -->
                    <span
                      v-if="child.color"
                      class="w-3 h-3 rounded-sm shrink-0"
                      :style="{ backgroundColor: child.color }"
                    />
                    <span class="font-medium truncate">{{ child.label }}</span>
                  </a>
                </router-link>
              </template>
            </SidebarSecondaryList>

            <!-- Inboxes (with type icons + status) -->
            <SidebarSecondaryList
              v-else-if="
                secondaryItem.hasSubMenu && secondaryItem.key === 'inboxes'
              "
              :key="secondaryItem.key"
              :secondary-item="secondaryItem"
              :children="secondaryItem.children"
              :show-all="showAllInboxes"
              :can-show-new-button="canShowNewButton(secondaryItem)"
              @toggle-show-all="toggleShowAllInboxes"
              @new-click="handleNewClick"
            >
              <template #item="{ items }">
                <SidebarInboxItem
                  v-for="child in items"
                  :key="child.id"
                  :inbox="child"
                  :inbox-types="INBOX_TYPES"
                  :get-inbox-status-color="getInboxStatusColor"
                  :get-inbox-status-text="getInboxStatusText"
                />
              </template>
              <template #item-remaining="{ items }">
                <SidebarInboxItem
                  v-for="child in items"
                  :key="child.id"
                  :inbox="child"
                  :inbox-types="INBOX_TYPES"
                  :get-inbox-status-color="getInboxStatusColor"
                  :get-inbox-status-text="getInboxStatusText"
                />
              </template>
            </SidebarSecondaryList>

            <!-- Contact labels, teams, filters, and default submenus -->
            <SidebarSecondaryList
              v-else-if="secondaryItem.hasSubMenu"
              :key="secondaryItem.key"
              :secondary-item="secondaryItem"
              :children="secondaryItem.children"
              :show-all="showAllMap[secondaryItem.key]"
              :can-show-new-button="canShowNewButton(secondaryItem)"
              @toggle-show-all="toggleShowAll(secondaryItem.key)"
              @new-click="handleNewClick"
            >
              <template #item="{ items }">
                <router-link
                  v-for="child in items"
                  :key="child.id"
                  v-slot="{
                    href: childHref,
                    isActive: childIsActive,
                    navigate: childNavigate,
                  }"
                  :to="child.toState"
                  custom
                >
                  <a
                    :href="childHref"
                    class="flex mt-1 items-center gap-3 px-4 py-2 rounded-lg text-primary-100 hover:bg-primary-900/40 transition-colors"
                    :class="{ 'bg-primary-900/40 text-white': childIsActive }"
                    @click="childNavigate"
                  >
                    <!-- `shrink-0`: tamanho fixo nao impede encolhimento em
                         flexbox, e o padrao de `flex-shrink` e 1. Sem ele, um
                         nome de etiqueta longo espremia o quadrado ate virar
                         uma barra vertical; quem tem de ceder e o texto, que ja
                         trunca. -->
                    <span
                      v-if="child.color"
                      class="w-3 h-3 rounded-sm shrink-0"
                      :style="{ backgroundColor: child.color }"
                    />
                    <span class="font-medium truncate">{{ child.label }}</span>
                  </a>
                </router-link>
              </template>
              <template #item-remaining="{ items }">
                <router-link
                  v-for="child in items"
                  :key="child.id"
                  v-slot="{
                    href: childHref,
                    isActive: childIsActive,
                    navigate: childNavigate,
                  }"
                  :to="child.toState"
                  custom
                >
                  <a
                    :href="childHref"
                    class="flex mt-1 items-center gap-3 px-4 py-2 rounded-lg text-primary-100 hover:bg-primary-900/40 transition-colors"
                    :class="{ 'bg-primary-900/40 text-white': childIsActive }"
                    @click="childNavigate"
                  >
                    <!-- `shrink-0`: tamanho fixo nao impede encolhimento em
                         flexbox, e o padrao de `flex-shrink` e 1. Sem ele, um
                         nome de etiqueta longo espremia o quadrado ate virar
                         uma barra vertical; quem tem de ceder e o texto, que ja
                         trunca. -->
                    <span
                      v-if="child.color"
                      class="w-3 h-3 rounded-sm shrink-0"
                      :style="{ backgroundColor: child.color }"
                    />
                    <span class="font-medium truncate">{{ child.label }}</span>
                  </a>
                </router-link>
              </template>
            </SidebarSecondaryList>

            <!-- Modal trigger item -->
            <a
              v-else-if="secondaryItem.showModalForNewItem"
              :key="secondaryItem.key"
              class="flex items-center gap-3 px-4 py-3 rounded-lg text-primary-100 hover:bg-primary-900/40 transition-colors cursor-pointer"
              @click="handleNewClick(secondaryItem)"
            >
              <fluent-icon :icon="secondaryItem.icon" />
              <span class="font-medium">{{
                $t(`SIDEBAR.${secondaryItem.label}`)
              }}</span>
            </a>

            <!-- Plain secondary item -->
            <SidebarSecondaryItem
              v-else
              :key="secondaryItem.key"
              :secondary-item="secondaryItem"
              :is-active="isSecondaryItemActive(secondaryItem, false)"
            />
          </template>
        </div>
      </template>
    </nav>

    <!-- Footer: Actions -->
    <SidebarFooter
      :is-collapsed="isCollapsed"
      :show-options-menu="showOptionsMenu"
      @toggle-options="toggleOptions"
      @toggleAccounts="toggleAccountModal"
      @showSupportChatWindow="toggleSupportChatWindow"
      @openKeyShortcutModal="$emit('openKeyShortcutModal')"
      @openNotificationPanel="openNotificationPanel"
    />
  </div>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 12px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  @apply bg-primary-900/60 rounded-lg border-4 border-solid border-transparent bg-clip-content;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  @apply bg-primary-900/80;
}
</style>
